/* ============================================================
   ProtoClinical: app.js
   Lenis · GSAP reveals · skeleton/image reveal · interactions · AJAX cart
   The buy flow (add / change) talks to Shopify's cart API and
   re-renders the drawer via the Section Rendering API. Nothing
   here gates a purchase behind motion.
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.PC_CONFIG || {};
  var MOTION = CFG.motion || {};
  var ROUTES = CFG.routes || {};
  const REDUCED = (typeof window.PC_REDUCED === "boolean") ? window.PC_REDUCED
    : window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const DESKTOP = window.matchMedia("(hover: hover) and (pointer: fine)").matches && window.innerWidth > 1000;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));

  /* ---------------------------------------------------------
     LENIS + GSAP
  --------------------------------------------------------- */
  function initMotion() {
    // The heavy GSAP / Lenis / ScrollTrigger layer runs on desktop pointers only.
    // On mobile/touch: and under reduced motion: it costs ~1s of main-thread
    // work (ScrollTrigger.create + refresh reflows + scrubbed parallax) for
    // effects that barely apply there. Content reveals come from the inline
    // 'reveal-boot' IntersectionObserver on every device, so skipping GSAP here
    // changes nothing about what the visitor sees, only what the CPU does.
    if (!(DESKTOP && !REDUCED && window.gsap && window.ScrollTrigger)) {
      // Safety net only: reveal-boot normally handles this (and keeps the nicer
      // on-scroll reveal). Un-hide here just in case that inline script never ran.
      if (!window.__pcRevealBoot) {
        $$("[data-reveal], .reveal, [data-stagger], .mask-headline").forEach((el) => el.classList.add("is-in"));
      }
      $$("[data-count]").forEach((el) => countUp(el, true));
      return;
    }

    let lenis = null;
    if (MOTION.smoothScroll !== false && window.Lenis) {
      lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 0.9, smoothWheel: true });
      function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
      window.__lenis = lenis;
    }

    gsap.registerPlugin(ScrollTrigger);
    if (lenis) lenis.on("scroll", ScrollTrigger.update);

    $$("[data-reveal], .reveal").forEach((el) => {
      ScrollTrigger.create({ trigger: el, start: "top 85%", once: true, onEnter: () => el.classList.add("is-in") });
    });
    $$("[data-stagger]").forEach((group) => {
      const kids = Array.from(group.children);
      kids.forEach((k, i) => { k.style.transitionDelay = (i * 80) + "ms"; });
      ScrollTrigger.create({
        trigger: group, start: "top 85%", once: true,
        onEnter: () => { group.classList.add("is-in"); setTimeout(() => kids.forEach((k) => { k.style.transitionDelay = ""; }), 1200); },
      });
    });
    $$(".mask-headline").forEach((el) => {
      ScrollTrigger.create({ trigger: el, start: "top 88%", once: true, onEnter: () => el.classList.add("is-in") });
    });
    $$("[data-count]").forEach((el) => {
      ScrollTrigger.create({ trigger: el, start: "top 90%", once: true, onEnter: () => countUp(el) });
    });
    $$("[data-parallax]").forEach((el) => {
      const amt = parseFloat(el.dataset.parallax) || 40;
      gsap.to(el, { yPercent: amt, ease: "none", scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: true } });
    });

    // ScrollTrigger measures each trigger's start position at creation time,
    // but lazy product/blog/collection images and late web-fonts shift the
    // layout afterwards: leaving those positions stale, so some grids never
    // cross their start and their images stay hidden on first load. Recompute
    // once the page (images + fonts) has actually settled.
    const refresh = () => ScrollTrigger.refresh();
    if (document.readyState === "complete") refresh();
    else window.addEventListener("load", refresh, { once: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    setTimeout(refresh, 800);
  }

  function countUp(el, instant) {
    const target = parseFloat(el.dataset.count);
    const dec = (el.dataset.count.split(".")[1] || "").length;
    const suffix = el.dataset.suffix || "";
    const prefix = el.dataset.prefix || "";
    if (instant) { el.textContent = prefix + target.toFixed(dec) + suffix; return; }
    const dur = 1400; const start = performance.now();
    function step(now) {
      const p = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + (target * e).toFixed(dec) + suffix;
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = prefix + target.toFixed(dec) + suffix;
    }
    requestAnimationFrame(step);
  }

  /* ---------------------------------------------------------
     HEADER · ANNOUNCEMENT · SEARCH · HERO · CAROUSELS · BA
  --------------------------------------------------------- */
  function initHeader() {
    const header = $("#siteHeader");
    if (!header || header.dataset.sticky === "false") return;
    const onScroll = () => header.classList.toggle("condensed", window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  function initHeaderOverflow() {
    const header = $("#siteHeader");
    const nav = header && $(".nav-left", header);
    const burger = nav && $("#burger", nav);
    const drawer = $("#mobileNav");
    if (!nav || !burger || !drawer) return;
    const items = $$(":scope > .nav-item.desktop", nav);
    const groups = $$(".drawer-body > .mobile-group", drawer);
    const closeButton = $("#mobileNavClose", drawer);
    let frame = null;

    function update() {
      frame = null;
      nav.setAttribute("data-overflow-ready", "");
      items.forEach((item) => { item.hidden = false; });
      burger.hidden = false;
      const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
      const widths = items.map((item) => item.getBoundingClientRect().width);
      const available = nav.getBoundingClientRect().width;
      const total = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, items.length - 1) * gap;
      let visibleCount = items.length;
      if (total > available) {
        let used = burger.getBoundingClientRect().width;
        visibleCount = 0;
        for (const width of widths) {
          if (used + gap + width > available) break;
          used += gap + width;
          visibleCount += 1;
        }
      }

      const hasOverflow = visibleCount < items.length;
      const focused = document.activeElement;
      items.forEach((item, index) => {
        item.hidden = index >= visibleCount;
        if (!item.hidden) return;
        const trigger = $(".nav-trigger", item);
        const mega = $(".mega", item);
        if (trigger) trigger.setAttribute("aria-expanded", "false");
        if (mega) mega.removeAttribute("data-open");
        if (item.contains(focused)) burger.focus();
      });
      if (groups.length === items.length) {
        groups.forEach((group, index) => {
          group.hidden = index < visibleCount;
          if (group.hidden && group.contains(focused) && hasOverflow && closeButton) closeButton.focus();
        });
      }
      if (!hasOverflow && drawer.hasAttribute("data-open")) {
        if (closeButton) closeButton.click();
        const target = items[0] && (items[0].matches("a") ? items[0] : $(".nav-trigger", items[0]));
        (target || $(".brand", header)).focus();
      } else if (!hasOverflow && focused === burger) {
        const target = items[0] && (items[0].matches("a") ? items[0] : $(".nav-trigger", items[0]));
        if (target) target.focus();
      }
      burger.hidden = !hasOverflow;
    }

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("resize", schedule, { passive: true });
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(schedule);
      [$(".header-row", header), $(".brand", header), $(".nav-right", header)].forEach((el) => { if (el) observer.observe(el); });
    }
    if (document.fonts) {
      document.fonts.ready.then(schedule);
      document.fonts.addEventListener("loadingdone", schedule);
    }
  }

  function initAnnounce() {
    const rot = $("#announceRot");
    const items = $$("#announceRot li");
    if (items.length < 2) return;
    const ms = parseInt(rot && rot.dataset.rotateMs, 10) || 3600;
    let i = 0;
    setInterval(() => {
      items[i].removeAttribute("data-active");
      i = (i + 1) % items.length;
      items[i].setAttribute("data-active", "");
    }, ms);
  }

  function initSearch() {
    const btn = $("#searchBtn");
    const panel = $("#searchPanel");
    if (!btn || !panel) return;
    const input = $(".search-input", panel);
    const open = () => {
      panel.setAttribute("data-open", "");
      btn.setAttribute("aria-expanded", "true");
      setTimeout(() => {
        if (panel.hasAttribute("data-open") && input) input.focus();
      }, 60);
    };
    const close = (restoreFocus = false) => {
      if (!panel.hasAttribute("data-open")) return;
      panel.removeAttribute("data-open");
      btn.setAttribute("aria-expanded", "false");
      if (restoreFocus) btn.focus();
    };
    btn.addEventListener("click", (e) => { e.stopPropagation(); panel.hasAttribute("data-open") ? close() : open(); });
    document.addEventListener("click", (e) => {
      if (panel.hasAttribute("data-open") && !panel.contains(e.target) && e.target !== btn && !btn.contains(e.target)) close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && panel.hasAttribute("data-open")) {
        e.preventDefault();
        close(true);
      }
    });
  }

  /* Desktop mega-nav: hover-with-delay so cursor can travel from
     trigger to panel without the menu collapsing mid-flight.          */
  function initMegaNav() {
    const CLOSE_DELAY = 180;

    $$('.nav-item').forEach(function (item) {
      const trigger = $('.nav-trigger', item);
      const mega = $('.mega', item);
      if (!trigger || !mega) return;
      let closeTimer = null;

      function openMega() {
        clearTimeout(closeTimer);
        $$('.mega[data-open]').forEach(function (m) {
          if (m === mega) return;
          m.removeAttribute('data-open');
          const t = $('.nav-trigger', m.closest('.nav-item'));
          if (t) t.setAttribute('aria-expanded', 'false');
        });
        mega.setAttribute('data-open', '');
        trigger.setAttribute('aria-expanded', 'true');
      }

      function closeMega(immediate) {
        clearTimeout(closeTimer);
        if (immediate) {
          mega.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          closeTimer = setTimeout(function () {
            mega.removeAttribute('data-open');
            trigger.setAttribute('aria-expanded', 'false');
          }, CLOSE_DELAY);
        }
      }

      item.addEventListener('mouseenter', openMega);
      item.addEventListener('mouseleave', function () { closeMega(false); });
      /* mega has pointer-events:auto when [data-open], so these fire: */
      mega.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
      mega.addEventListener('mouseleave', function () { closeMega(false); });

      trigger.addEventListener('click', function () {
        mega.hasAttribute('data-open') ? closeMega(true) : openMega();
      });

      trigger.addEventListener('focus', openMega);
      mega.addEventListener('focusin', openMega);
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) closeMega(true);
      });

      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { closeMega(true); trigger.focus(); }
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.nav-item')) {
        $$('.mega[data-open]').forEach(function (m) {
          m.removeAttribute('data-open');
          const t = $('.nav-trigger', m.closest('.nav-item'));
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  /* Progressive reveal: hide collections beyond INITIAL and inject a
     "See N more" toggle button in each mega column that has many items. */
  function initMegaSeeMore() {
    const INITIAL = 10;

    $$('.mega-col').forEach(function (col) {
      const list = $('ul', col);
      if (!list) return;
      const items = Array.from(list.querySelectorAll(':scope > li'));
      if (items.length <= INITIAL) return;

      const extra = items.slice(INITIAL);
      extra.forEach(function (li) { li.classList.add('mega-hidden'); });

      const count = extra.length;
      const btn = document.createElement('button');
      btn.className = 'mega-see-more';
      btn.type = 'button';
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML = 'See ' + count + ' more <span class="ar" aria-hidden="true">→</span>';
      list.insertAdjacentElement('afterend', btn);

      btn.addEventListener('click', function () {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        extra.forEach(function (li) { li.classList.toggle('mega-hidden', expanded); });
        btn.innerHTML = expanded
          ? 'See ' + count + ' more <span class="ar" aria-hidden="true">→</span>'
          : 'See less <span class="ar" aria-hidden="true">↑</span>';
      });
    });
  }

  function initHero() {
    const root = $("[data-hero]") || document;
    const slides = $$(".hero-slide", root);
    const eyebrows = $$(".hero-eyebrow", root);
    const claims = $$(".hero-claim", root);
    const dots = $$(".hero-dot", root);
    if (!slides.length) return;
    let i = 0; let timer;
    const span = parseInt(root.dataset ? root.dataset.autoplay : 0, 10) || 6000;
    function go(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, k) => s.toggleAttribute("data-active", k === i));
      eyebrows.forEach((s, k) => s.toggleAttribute("data-active", k === i));
      claims.forEach((s, k) => s.toggleAttribute("data-active", k === i));
      dots.forEach((d, k) => { d.removeAttribute("data-active"); if (k === i) { void d.offsetWidth; d.setAttribute("data-active", ""); } });
    }
    function loop() { clearTimeout(timer); if (slides.length < 2 || REDUCED) return; timer = setTimeout(() => { go(i + 1); loop(); }, span); }
    dots.forEach((d, k) => d.addEventListener("click", () => { go(k); loop(); }));
    $("#heroPrev") && $("#heroPrev").addEventListener("click", () => { go(i - 1); loop(); });
    $("#heroNext") && $("#heroNext").addEventListener("click", () => { go(i + 1); loop(); });
    go(0); loop();
  }

  function initCarousels() {
    $$(".carousel").forEach((car) => {
      if (car.dataset.carInit) return;
      car.dataset.carInit = "1";
      const track = $(".carousel-track", car);
      const head = car.closest("section") || car.parentElement;
      const prev = head ? $(".car-prev", head) : null;
      const next = head ? $(".car-next", head) : null;
      if (!track) return;
      const step = () => Math.max(260, track.clientWidth * 0.8);
      const update = () => {
        if (!prev || !next) return;
        prev.disabled = track.scrollLeft < 8;
        next.disabled = track.scrollLeft > track.scrollWidth - track.clientWidth - 8;
      };
      prev && prev.addEventListener("click", () => track.scrollBy({ left: -step(), behavior: "smooth" }));
      next && next.addEventListener("click", () => track.scrollBy({ left: step(), behavior: "smooth" }));
      track.addEventListener("scroll", update, { passive: true });
      update();
      let down = false, sx = 0, sl = 0, moved = false;

      // Attach pointermove and pointerup/cancel listeners dynamically on pointerdown
      // to avoid idle main-thread listener overhead across all carousels on every mouse movement.
      const onPointerMove = (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = sl - dx;
      };

      const onPointerEnd = () => {
        if (!down) return;
        down = false;
        track.classList.remove("dragging");
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerEnd);
        window.removeEventListener("pointercancel", onPointerEnd);
      };

      track.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "touch" || e.target.closest("button, a, video, input, select, textarea")) return;
        down = true; moved = false; sx = e.clientX; sl = track.scrollLeft; track.classList.add("dragging");
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerEnd);
        window.addEventListener("pointercancel", onPointerEnd);
      });
      track.addEventListener("click", (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
  }
  window.PCInitCarousels = initCarousels;

  function initBeforeAfter() {
    $$(".ba").forEach((ba) => {
      const before = $(".ba-before", ba);
      const handle = $(".ba-handle", ba);
      if (!before || !handle) return;
      let dragging = false, rect = null;
      const invalidateRect = () => { rect = null; };

      // Cache bounding rect on drag start to prevent synchronous layout reflows (layout thrashing) on high-frequency pointermove events.
      function set(clientX) {
        if (!rect) rect = ba.getBoundingClientRect();
        let p = (clientX - rect.left) / (rect.width || 1);
        p = Math.max(0.02, Math.min(0.98, p));
        before.style.clipPath = `inset(0 ${(1 - p) * 100}% 0 0)`;
        handle.style.left = (p * 100) + "%";
      }
      const move = (e) => { if (!dragging) return; set((e.touches ? e.touches[0] : e).clientX); };
      const end = () => {
        if (!dragging) return;
        dragging = false;
        rect = null;
        ba.classList.remove("dragging");
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
        window.removeEventListener("scroll", invalidateRect, true);
        window.removeEventListener("resize", invalidateRect);
      };
      const startDrag = (e) => {
        if (dragging) return;
        dragging = true;
        rect = ba.getBoundingClientRect();
        ba.classList.add("dragging");
        set((e.touches ? e.touches[0] : e).clientX);
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", end);
        window.addEventListener("pointercancel", end);
        window.addEventListener("scroll", invalidateRect, { capture: true, passive: true });
        window.addEventListener("resize", invalidateRect, { passive: true });
      };
      handle.addEventListener("pointerdown", startDrag);
      ba.addEventListener("pointerdown", (e) => { if (e.target.closest(".hotspot")) return; startDrag(e); });
      handle.setAttribute("tabindex", "0");
      handle.setAttribute("role", "slider");
      handle.setAttribute("aria-label", "Reveal glass-skin result");
      let kp = 50;
      handle.addEventListener("keydown", (e) => {
        if (e.key === "ArrowLeft") kp = Math.max(2, kp - 4);
        else if (e.key === "ArrowRight") kp = Math.min(98, kp + 4);
        else return;
        e.preventDefault();
        before.style.clipPath = `inset(0 ${100 - kp}% 0 0)`;
        handle.style.left = kp + "%";
      });
    });
  }

  /* ---------------------------------------------------------
     CART: Shopify AJAX + Section Rendering API
  --------------------------------------------------------- */
  function bumpCount(delta) {
    $$("[data-cart-count]").forEach((el) => {
      const n = Math.max(0, (parseInt(el.textContent, 10) || 0) + delta);
      el.textContent = n;
      el.hidden = n <= 0;
    });
  }
  function setCount(n) {
    $$("[data-cart-count]").forEach((el) => { el.textContent = n; el.hidden = n <= 0; });
  }

  async function renderCartSections(response) {
    const html = response && response.sections && response.sections["cart-drawer"];
    if (html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("#CartContents");
      const current = $("#CartContents");
      if (fresh && current) {
        current.replaceWith(fresh);
        const total = fresh.getAttribute("data-cart-total-count");
        if (total != null) setCount(parseInt(total, 10) || 0);
        const dc = $("#drawerCount");
        const freshDc = doc.querySelector("#drawerCount");
        if (dc && freshDc) dc.textContent = freshDc.textContent;
        window.dispatchEvent(new Event("pc:cart:rendered"));
        return;
      }
    }
    // fallback: pull a fresh drawer section
    try {
      const r = await fetch(`${ROUTES.root || "/"}?sections=cart-drawer`);
      const data = await r.json();
      const doc = new DOMParser().parseFromString(data["cart-drawer"], "text/html");
      const fresh = doc.querySelector("#CartContents");
      const current = $("#CartContents");
      if (fresh && current) { current.replaceWith(fresh); const total = fresh.getAttribute("data-cart-total-count"); if (total != null) setCount(parseInt(total, 10) || 0); }
      window.dispatchEvent(new Event("pc:cart:rendered"));
    } catch (e) { /* drawer stays as-is; cart still mutated server-side */ }
  }

  const Cart = {
    async add(items, fromEl) {
      if (!Array.isArray(items)) items = [items];
      const totalQty = items.reduce((s, i) => s + (i.quantity || 1), 0);
      bumpCount(totalQty); // optimistic
      if (fromEl) { fromEl.classList.add("loading"); }
      try {
        const res = await fetch(ROUTES.cartAdd, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items, sections: ["cart-drawer"], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok) throw data;
        await renderCartSections(data);
        openDrawer();
        if (fromEl) { fromEl.classList.remove("loading"); fromEl.classList.add("added"); setTimeout(() => fromEl.classList.remove("added"), 1200); }
        window.dispatchEvent(new CustomEvent("pc:add", { detail: { from: fromEl, data } }));
        return { ok: true, data };
      } catch (err) {
        bumpCount(-totalQty); // revert
        if (fromEl) fromEl.classList.remove("loading");
        toast((err && err.description) || (CFG.strings && CFG.strings.unavailable) || "Could not add to cart");
        return { ok: false, error: err };
      }
    },
    async change(line, quantity, previousQuantity) {
      try {
        const res = await fetch(ROUTES.cartChange, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: line, quantity, sections: ["cart-drawer"], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        await renderCartSections(data);
        if (typeof data.item_count === "number") setCount(data.item_count);
        if (Number.isFinite(previousQuantity) && quantity > previousQuantity) {
          window.dispatchEvent(new CustomEvent("pc:add", {
            detail: { quantityDelta: quantity - previousQuantity },
          }));
        }
      } catch (e) { /* no-op */ }
    },
  };
  window.PCCart = Cart;

  function toast(msg) {
    let t = $("#pcToast");
    if (!t) { t = document.createElement("div"); t.id = "pcToast"; t.className = "pc-toast"; document.body.appendChild(t); }
    t.textContent = msg; t.setAttribute("data-show", "");
    clearTimeout(t._timer); t._timer = setTimeout(() => t.removeAttribute("data-show"), 3200);
  }

  function openDrawer() {
    const d = $("#cartDrawer"), s = $("#cartScrim");
    if (!d || CFG.cartType === "page") { if (CFG.cartType === "page") window.location.href = ROUTES.cart; return; }
    s && s.setAttribute("data-open", ""); d.setAttribute("data-open", "");
    document.body.style.overflow = "hidden"; if (window.__lenis) window.__lenis.stop();
    const close = $("#drawerClose", d); close && close.focus();
  }
  function closeDrawer() {
    const d = $("#cartDrawer"), s = $("#cartScrim");
    s && s.removeAttribute("data-open"); d && d.removeAttribute("data-open");
    document.body.style.overflow = ""; if (window.__lenis) window.__lenis.start();
  }
  window.PCOpenCart = openDrawer;

  function collectAddItems(scope) {
    return $$("[data-add-item][data-variant-id]", scope).map((el) => ({
      id: el.dataset.variantId, quantity: parseInt(el.dataset.quantity, 10) || 1,
    }));
  }

  function initCart() {
    $("#cartBtn") && $("#cartBtn").addEventListener("click", (e) => {
      if (CFG.cartType === "page") return; // let the link go to /cart
      e.preventDefault(); openDrawer();
    });
    document.addEventListener("click", (e) => {
      const close = e.target.closest("#drawerClose, #cartScrim");
      if (close) { e.preventDefault(); closeDrawer(); return; }

      const add = e.target.closest("[data-add][data-variant-id]");
      if (add && add.tagName === "BUTTON") {
        e.preventDefault();
        Cart.add({ id: add.dataset.variantId, quantity: parseInt(add.dataset.quantity, 10) || 1 }, add);
        return;
      }
      const addAll = e.target.closest("[data-add-all]");
      if (addAll) {
        e.preventDefault();
        const scope = addAll.closest("[data-add-all-scope]") || document;
        const items = collectAddItems(scope);
        if (items.length) Cart.add(items, addAll);
        return;
      }
      const qtyBtn = e.target.closest("[data-qty-change]");
      if (qtyBtn) {
        e.preventDefault();
        const qtyScope = qtyBtn.closest(".qty");
        const qtyValue = qtyScope && $("[data-qty-value]", qtyScope);
        const previousQuantity = qtyValue ? parseInt(qtyValue.textContent, 10) : NaN;
        Cart.change(qtyBtn.dataset.line, parseInt(qtyBtn.dataset.qty, 10), previousQuantity);
        return;
      }
      // cart PAGE stepper: adjust the quantity input and submit the form
      const step = e.target.closest("[data-cart-step]");
      if (step) {
        e.preventDefault();
        const wrap = step.closest(".qty");
        const input = wrap && wrap.querySelector("input");
        if (input) {
          input.value = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.cartStep, 10));
          const form = step.closest("form");
          const updateBtn = form && form.querySelector('[name="update"]');
          if (form && form.requestSubmit && updateBtn) form.requestSubmit(updateBtn);
          else if (form) form.submit();
        }
        return;
      }
    });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeDrawer(); });
  }

  /* ---------------------------------------------------------
     MOBILE NAV
  --------------------------------------------------------- */
  function initMobileNav() {
    const burger = $("#burger");
    const nav = $("#mobileNav");
    if (!burger || !nav) return;
    let focusReturn = null;

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      const focusableElements = Array.from(nav.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hasAttribute("hidden") && element.getClientRects().length > 0);
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      if (!firstFocusable) return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }

    const open = () => {
      focusReturn = document.activeElement;
      nav.setAttribute("data-open", "");
      burger.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
      const closeBtn = $("#mobileNavClose", nav);
      if (closeBtn) closeBtn.focus();
      document.addEventListener("keydown", handleKeydown);
    };

    const close = () => {
      nav.removeAttribute("data-open");
      burger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      if (focusReturn) focusReturn.focus();
      document.removeEventListener("keydown", handleKeydown);
    };

    const handleKeydown = (e) => {
      if (e.key === "Escape") close();
      else trapFocus(e);
    };

    burger.addEventListener("click", () => nav.hasAttribute("data-open") ? close() : open());
    $("#mobileNavClose") && $("#mobileNavClose").addEventListener("click", close);
    nav.addEventListener("click", (e) => { if (e.target === nav) close(); });
    $$("#mobileNav a").forEach((a) => a.addEventListener("click", close));
  }

  function dropletSVG() {
    return `<svg viewBox="0 0 30 38" width="30" height="38"><g class="drop-body"><path d="M15 2 C15 2 27 16 27 25 A12 12 0 0 1 3 25 C3 16 15 2 15 2Z" fill="rgba(233,180,172,.5)" stroke="#DE3573" stroke-width="1"/><ellipse class="drop-shine" cx="11" cy="22" rx="3" ry="5" fill="rgba(255,255,255,.85)"/></g></svg>`;
  }
  window.__dropletSVG = dropletSVG;

  /* ---------------------------------------------------------
     BOOT
     Note: the critical reveal (un-hiding server-rendered content),
     per-image skeleton shimmer and hero-skeleton dismissal run from
     the inline 'reveal-boot' snippet at parse time: NOT here: so
     they never wait on the deferred GSAP bundles. GSAP/ScrollTrigger
     below layer richer scroll effects on top (idempotent).
  --------------------------------------------------------- */
  function boot() {
    initHeader();
    initAnnounce();
    initSearch();
    initMegaNav();
    initMegaSeeMore();
    initHero();
    initCarousels();
    initBeforeAfter();
    initCart();
    initMobileNav();
    initHeaderOverflow();
    initMotion();
    window.__pcReady = true;
    window.dispatchEvent(new Event("pc:ready"));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
